import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { RootLayout } from '@/components/layout/RootLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoginForm } from '@/components/auth/LoginForm';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { ResetPasswordPage } from '@/components/auth/ResetPasswordPage';
import { AuthCallbackPage } from '@/components/auth/AuthCallbackPage';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { NotFound } from '@/components/layout/NotFound';

const Dashboard = lazy(() =>
  import('@/components/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const OrganizationManager = lazy(() =>
  import('@/components/organizations/OrganizationManager').then((m) => ({
    default: m.OrganizationManager,
  })),
);
const GroupManager = lazy(() =>
  import('@/components/groups/GroupManager').then((m) => ({ default: m.GroupManager })),
);
const UserManagement = lazy(() =>
  import('@/components/users/UserManagement').then((m) => ({ default: m.UserManagement })),
);
const PublicUploadPage = lazy(() =>
  import('@/components/upload/PublicUploadPage').then((m) => ({ default: m.PublicUploadPage })),
);
const PublicSharePage = lazy(() =>
  import('@/components/share/PublicSharePage').then((m) => ({ default: m.PublicSharePage })),
);
const UploadLinksManager = lazy(() =>
  import('@/components/upload/UploadLinksManager').then((m) => ({
    default: m.UploadLinksManager,
  })),
);
const NotificationSettings = lazy(() =>
  import('@/components/settings/NotificationSettings').then((m) => ({
    default: m.NotificationSettings,
  })),
);
const SettingsLayout = lazy(() =>
  import('@/components/settings/SettingsLayout').then((m) => ({
    default: m.SettingsLayout,
  })),
);
const BrandingForm = lazy(() =>
  import('@/components/settings/BrandingForm').then((m) => ({
    default: m.BrandingForm,
  })),
);
const WorkspaceProfileForm = lazy(() =>
  import('@/components/settings/WorkspaceProfileForm').then((m) => ({
    default: m.WorkspaceProfileForm,
  })),
);
const PlanSettings = lazy(() =>
  import('@/components/settings/PlanSettings').then((m) => ({
    default: m.PlanSettings,
  })),
);
const DocumentManager = lazy(() =>
  import('@/components/documents/DocumentManager').then((m) => ({ default: m.DocumentManager })),
);
const DocumentTypeManager = lazy(() =>
  import('@/components/documents/DocumentTypeManager').then((m) => ({
    default: m.DocumentTypeManager,
  })),
);
const OrgTypeManager = lazy(() =>
  import('@/components/organizations/OrgTypeManager').then((m) => ({
    default: m.OrgTypeManager,
  })),
);
const ProductManager = lazy(() =>
  import('@/components/products/ProductManager').then((m) => ({
    default: m.ProductManager,
  })),
);
const AuditLog = lazy(() =>
  import('@/components/analytics/AuditLog').then((m) => ({ default: m.AuditLog })),
);
const EvaluationFormsManager = lazy(() =>
  import('@/components/evaluation-forms/EvaluationFormsManager').then((m) => ({
    default: m.EvaluationFormsManager,
  })),
);
const EvaluationFormEditor = lazy(() =>
  import('@/components/evaluation-forms/EvaluationFormEditor').then((m) => ({
    default: m.EvaluationFormEditor,
  })),
);

function LazyFallback() {
  return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>;
}

const workspaceRouter = createBrowserRouter([
  {
    path: '/upload/:token',
    element: (
      <Lazy>
        <PublicUploadPage />
      </Lazy>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/share/:token',
    element: (
      <Lazy>
        <PublicSharePage />
      </Lazy>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'login', element: <LoginForm /> },
      { path: 'forgot-password', element: <ForgotPasswordForm /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'auth/callback', element: <AuthCallbackPage /> },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <Lazy>
              <Dashboard />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'organizations',
        element: (
          <ProtectedRoute>
            <Lazy>
              <OrganizationManager />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        // CML-204: organization detail is a real route so refresh / direct URL
        // paste reloads the same org. Same component as the list — it reads the
        // :id param to select the detail view.
        path: 'organizations/:id',
        element: (
          <ProtectedRoute>
            <Lazy>
              <OrganizationManager />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'groups',
        element: (
          <ProtectedRoute>
            <Lazy>
              <GroupManager />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'documents',
        element: (
          <ProtectedRoute>
            <Lazy>
              <DocumentManager />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'evaluation-forms',
        element: (
          <ProtectedRoute requiredRole="super_admin">
            <Lazy>
              <EvaluationFormsManager />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'evaluation-forms/:id',
        element: (
          <ProtectedRoute requiredRole="super_admin">
            <Lazy>
              <EvaluationFormEditor />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'upload-links',
        element: (
          <ProtectedRoute requiredRole="manager">
            <Lazy>
              <UploadLinksManager />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'users',
        element: (
          <ProtectedRoute requiredRole="account_admin">
            <Lazy>
              <UserManagement />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        path: 'audit-log',
        element: (
          <ProtectedRoute requiredRole="account_admin">
            <Lazy>
              <AuditLog />
            </Lazy>
          </ProtectedRoute>
        ),
      },
      {
        // Layout is reachable by ANY authenticated user so they can manage
        // their own /settings/notifications. Each admin-only child below is
        // individually gated with requiredRole="account_admin" (S11-5).
        path: 'settings',
        element: (
          <ProtectedRoute>
            <Lazy>
              <SettingsLayout />
            </Lazy>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/settings/notifications" replace /> },
          {
            path: 'notifications',
            element: (
              <Lazy>
                <NotificationSettings />
              </Lazy>
            ),
          },
          {
            path: 'branding',
            element: (
              <ProtectedRoute requiredRole="account_admin">
                <Lazy>
                  <BrandingForm />
                </Lazy>
              </ProtectedRoute>
            ),
          },
          {
            path: 'document-types',
            element: (
              <ProtectedRoute requiredRole="account_admin">
                <Lazy>
                  <DocumentTypeManager />
                </Lazy>
              </ProtectedRoute>
            ),
          },
          {
            path: 'org-types',
            element: (
              <ProtectedRoute requiredRole="account_admin">
                <Lazy>
                  <OrgTypeManager />
                </Lazy>
              </ProtectedRoute>
            ),
          },
          {
            path: 'products',
            element: (
              <ProtectedRoute requiredRole="account_admin">
                <Lazy>
                  <ProductManager />
                </Lazy>
              </ProtectedRoute>
            ),
          },
          {
            path: 'workspace',
            element: (
              <ProtectedRoute requiredRole="account_admin">
                <Lazy>
                  <WorkspaceProfileForm />
                </Lazy>
              </ProtectedRoute>
            ),
          },
          {
            path: 'plan',
            element: (
              <ProtectedRoute requiredRole="account_admin">
                <Lazy>
                  <PlanSettings />
                </Lazy>
              </ProtectedRoute>
            ),
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function WorkspaceRoutes() {
  return <RouterProvider router={workspaceRouter} />;
}
